import { Palette } from "@mui/material";
import { IChatBlock } from "@pages/askDost/types";
import { buildResolutionFromState } from "@pages/askDost/utils/utils";
import { ChartBlock } from "./ChartBlock";
import { CodeBlock } from "./CodeBlock";
import { ConfirmRequestBlock } from "./ConfirmRequestBlock";
// import { DocumentsStatusBlock } from "../document/DocumentsStatusBlock";
import { ErrorBlock } from "./ErrorBlock";
import { FileBlock } from "./FileBlock";
import { MappingWorkflowBlock } from "./MappingWorkflowBlock";
import { McqBlock } from "./McqBlock";
import { MessageMarkdown } from "./MessageMarkdown";
import { TableBlock } from "./TableBlock";
import { ThinkingBlock } from "./ThinkingBlock";

const getActionType = (
  isWorkflow: boolean,
  valueKind: string,
  translate: (text: string, options?: any) => string,
) => {
  if (isWorkflow) return undefined;
  if (valueKind === "formula") return translate("formula_action");
  if (valueKind === "direct") return translate("set_direct_action");
  return translate("map_action");
};

const getConfirmResolution = (
  block: any,
  entry: any,
  historyResolution: any,
  ruleEntriesLength: number,
) => {
  if (block?.itemResolutions?.[entry?.write_id]) {
    return block.itemResolutions[entry.write_id];
  }
  if (historyResolution) {
    return historyResolution;
  }
  if (ruleEntriesLength === 1) {
    return block?.confirmResolution;
  }
  return undefined;
};

const getMappingName = (
  block: any,
  innerBlock: any,
  entry: any,
  translate: (text: string, options?: any) => string,
) => {
  if (block?.confirmSummary) return block?.confirmSummary;
  if (innerBlock?.summary) return innerBlock?.summary;
  if (entry?.rules?.name) return entry?.rules?.name;
  return translate("mapping_rule_fallback");
};

const adaptConfirmToMapping = (
  block: IChatBlock,
  translate: (text: string, options?: any) => string,
): IChatBlock => {
  const innerBlock =
    (block as any)?.t === "artifact" ? (block as any)?.a : block;

  const ruleEntries = block?.mappingRuleEntries?.length
    ? block?.mappingRuleEntries
    : [
        {
          write_id: innerBlock?.write_id || innerBlock?.id,
          rules: innerBlock?.rules || {},
          state: innerBlock?.state,
          note: innerBlock?.note,
        },
      ];

  const isWorkflow = !!ruleEntries?.[0]?.rules?.kind?.includes("workflow");

  return {
    ...block,
    mappingWorkflow: {
      mode: "create",
      kind: isWorkflow ? "workflow" : "mapping",
      items:
        ruleEntries?.map((entry: any) => {
          const historyResolution = buildResolutionFromState(entry?.state, {
            summary: entry?.note,
          });

          return {
            write_id: entry?.write_id || "",
            confirmResolution: getConfirmResolution(
              block,
              entry,
              historyResolution,
              ruleEntries?.length || 0,
            ),
            name: getMappingName(block, innerBlock, entry, translate),
            orderNote: translate("applies_to_incoming_documents"),
            orderTag: entry?.rules?.kind || translate("rule_fallback"),
            conditions:
              entry?.rules?.conditions?.map((c: any) => ({
                field: c?.field || translate("field_fallback"),
                op: c?.operator || c?.op || "==",
                value: c?.value || "",
              })) || [],
            actions:
              entry?.rules?.results?.map((r: any) => ({
                type: getActionType(isWorkflow, r?.value_kind, translate),
                target: r?.target || translate("unknown_target_fallback"),
                value: r?.value || "",
              })) || [],
          };
        }) || [],
    },
  };
};

export const renderBlock = (
  block: IChatBlock,
  palette: Palette,
  translate: (text: string, options?: any) => string,
  onEditInstructions?: () => void,
  isStreaming?: boolean,
  onSendMessage?: (message: string) => void,
) => {
  const blockType = block?.type || (block as any)?.t;
  const actualType =
    blockType === "artifact" ? (block as any)?.a?.t : blockType;

  switch (actualType as string) {
    case "thinking":
    case "tool":
      return (
        <ThinkingBlock
          block={block}
          palette={palette}
          translate={translate}
          isStreaming={!!isStreaming}
        />
      );
    case "text":
      return (
        <MessageMarkdown content={block?.content || ""} palette={palette} />
      );
    case "table":
      return (
        <TableBlock block={block} palette={palette} translate={translate} />
      );
    case "chart":
      return (
        <ChartBlock block={block} palette={palette} translate={translate} />
      );

    case "write":
    case "confirm_request": {
      const innerBlock = blockType === "artifact" ? (block as any)?.a : block;

      if (innerBlock?.rules || block?.mappingRuleEntries) {
        const adaptedBlock = adaptConfirmToMapping(block, translate);
        return (
          <MappingWorkflowBlock
            block={adaptedBlock}
            palette={palette}
            translate={translate}
            onEditInstructions={onEditInstructions}
          />
        );
      }

      const adaptedConfirmBlock = {
        ...block,
        confirmResolution:
          block?.confirmResolution ||
          buildResolutionFromState(innerBlock?.state, {
            summary: innerBlock?.note,
          }),
      };

      return (
        <ConfirmRequestBlock
          block={adaptedConfirmBlock as IChatBlock}
          palette={palette}
          translate={translate}
          onEditInstructions={onEditInstructions}
        />
      );
    }

    case "error":
      return <ErrorBlock content={block?.content} palette={palette} />;

    case "mapping_workflow":
      return (
        <MappingWorkflowBlock
          block={block}
          palette={palette}
          translate={translate}
          onEditInstructions={onEditInstructions}
        />
      );
    case "mcq":
      return (
        <McqBlock
          block={block}
          palette={palette}
          translate={translate}
          onOptionSelect={onSendMessage}
        />
      );
    // case "docs_status":
    //   return (
    //     <DocumentsStatusBlock
    //       block={block}
    //       palette={palette}
    //       translate={translate}
    //       onActionClick={onSendMessage}
    //     />
    //   );
    case "code":
      return (
        <CodeBlock block={block} palette={palette} translate={translate} />
      );
    case "file":
      if (!block?.fileId || !block?.fileName) return null;
      return (
        <FileBlock block={block} palette={palette} translate={translate} />
      );

    default:
      return null;
  }
};
